import { useState } from "react";

import { endOfToday, parse } from "date-fns";
import { CustomComponents } from "react-day-picker";
import { twJoin } from "tailwind-merge";

import { Calendar } from "src/base-components/DateRangePicker";
import { DropDownElement } from "src/base-components/DropDown";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";

export type { DateRange } from "react-day-picker";

const NUMBER_OF_MONTHS = 1;
const HOURS_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  key: String(i + 1),
  value: String(i + 1).padStart(2, "0"),
})) as DropDownElement<string>[];
const MINUTES_OPTIONS = Array.from({ length: 60 }, (_, i) => ({
  key: String(i),
  value: String(i).padStart(2, "0"),
})) as DropDownElement<string>[];

const AM_PM_OPTIONS = [
  { key: "AM", value: "AM" },
  { key: "PM", value: "PM" },
] as DropDownElement<string>[];

export type DateTimePickerProps = {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  borderless?: boolean;
  disabled?: boolean;
  errored?: boolean;
  dateOnly?: boolean;
  allowFutureDates?: boolean;
};

const extractTime = (date: Date | undefined): TimePart => {
  if (!date) {
    return {
      hours: "12",
      minutes: "0",
      ampm: "AM",
    };
  }

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;
  return {
    hours: String(hours12),
    minutes: String(minutes),
    ampm,
  };
};

const mergeTime = (date: Date | undefined, time: TimePart | undefined) => {
  if (!date) return undefined;

  if (time) {
    const newDate = new Date(date);
    const parsedTime = parse(
      `${time.hours}:${time.minutes} ${time.ampm}`,
      "hh:mm a",
      new Date(),
    );

    newDate.setHours(parsedTime.getHours());
    newDate.setMinutes(parsedTime.getMinutes());
    return newDate;
  } else {
    // if its date only, we need adjust the time so that its in utc
    const offset = date.getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() - offset);
  }
};

type TimePart = {
  hours: string;
  minutes: string;
  ampm: string;
};

const YearMonthDropdown: CustomComponents["Dropdown"] = ({
  options,
  onChange,
  value,
  disabled,
  ...props
}) => {
  const elements = options?.map((option) => ({
    key: String(option.value),
    value: props.className?.includes("months")
      ? option.label.substring(0, 3)
      : option.label,
    disabled: option.disabled,
  }));

  const dataLoc = props.className?.includes("months")
    ? "date-time-picker-month"
    : "date-time-picker-year";

  return (
    <div className={twJoin(props.className, "flex-1")}>
      <FixedSimpleDropdown
        buttonDataLoc={`${dataLoc}-button`}
        dataLoc={dataLoc}
        disabled={disabled}
        elements={elements ?? []}
        placeholder={props.className?.includes("months") ? "Month" : "Year"}
        portal={false}
        selectedKey={String(value)}
        onSelect={(newValue) => {
          const evt = {
            target: { value: newValue },
          } as React.ChangeEvent<HTMLSelectElement>;
          onChange?.(evt);
        }}
      />
    </div>
  );
};

export const DateTimePicker = ({
  value,
  onChange,
  disabled = false,
  dateOnly = false,
  allowFutureDates = false,
}: DateTimePickerProps) => {
  const [time, _setTime] = useState(
    dateOnly ? { hours: "12", minutes: "0", ampm: "AM" } : extractTime(value),
  );

  const setTime = (newTime: Partial<TimePart>) => {
    const timeToSet = { ...time, ...newTime };
    _setTime(timeToSet);

    if (value) {
      onChange(mergeTime(value, dateOnly ? undefined : timeToSet));
    }
  };

  let disabledDates: { after: Date } | boolean | undefined;
  if (disabled) {
    disabledDates = disabled;
  } else if (allowFutureDates) {
    disabledDates = undefined;
  } else {
    disabledDates = { after: endOfToday() };
  }

  return (
    <>
      <Calendar
        captionLayout="dropdown"
        classNames={{
          months: "relative scroll-pr-4",
          nav: "flex items-center justify-between pt-1 w-60",
          month_caption:
            "flex justify-center items-center absolute top-0 left-6.5",
          dropdowns:
            "grow flex gap-x-1.5 items-center justify-center w-[188px]",
          caption: "scroll-px-2",
        }}
        components={{
          Dropdown: YearMonthDropdown,
        }}
        defaultMonth={value ?? new Date()}
        disabled={disabledDates}
        endMonth={allowFutureDates ? new Date(2100, 11) : endOfToday()}
        mode="single"
        numberOfMonths={NUMBER_OF_MONTHS}
        selected={value}
        startMonth={new Date(1950, 1)}
        autoFocus
        onSelect={(day) =>
          onChange(mergeTime(day, dateOnly ? undefined : time))
        }
      />
      {!dateOnly && (
        <div className="flex items-center gap-x-2 px-6 pb-6">
          <div className="flex-1">
            <FixedSimpleDropdown
              buttonDataLoc="date-time-picker-hours"
              disabled={disabled}
              elements={HOURS_OPTIONS}
              placeholder="HH"
              selectedKey={time.hours}
              onSelect={(key) => {
                setTime({
                  hours: key,
                });
              }}
            />
          </div>
          <span>:</span>
          <div className="flex-1">
            <FixedSimpleDropdown
              buttonDataLoc="date-time-picker-minutes"
              disabled={disabled}
              elements={MINUTES_OPTIONS}
              placeholder="MM"
              selectedKey={time.minutes}
              onSelect={(key) =>
                setTime({
                  minutes: key,
                })
              }
            />
          </div>
          <div>
            <FixedSimpleDropdown
              buttonDataLoc="date-time-picker-ampm"
              disabled={disabled}
              elements={AM_PM_OPTIONS}
              placeholder="AM"
              selectedKey={time.ampm}
              onSelect={(key) =>
                setTime({
                  ampm: key,
                })
              }
            />
          </div>
        </div>
      )}
    </>
  );
};
